import { useState } from "react";
import { PageHero, SectionLabel } from "../components/ui-kit";
import { Reveal } from "../components/motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Activity, Flame, Beef, Droplet, Scale, Dumbbell } from "lucide-react";

const inputCls = "w-full bg-secondary/50 border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-accent transition-colors";
const Result = ({ children }) => <div className="mt-5 glass rounded-xl p-5 text-center"><p className="font-heading font-black text-2xl text-accent">{children}</p></div>;

function BMICalc() {
  const [h, setH] = useState(""); const [w, setW] = useState("");
  const bmi = h && w ? (w / ((h / 100) ** 2)).toFixed(1) : null;
  const cat = bmi ? (bmi < 18.5 ? "Underweight" : bmi < 25 ? "Healthy" : bmi < 30 ? "Overweight" : "Obese") : "";
  return (<div className="space-y-4"><input className={inputCls} placeholder="Height (cm)" type="number" value={h} onChange={(e)=>setH(e.target.value)} data-testid="bmi-height" /><input className={inputCls} placeholder="Weight (kg)" type="number" value={w} onChange={(e)=>setW(e.target.value)} data-testid="bmi-weight" />{bmi && <Result>BMI {bmi} · {cat}</Result>}</div>);
}

function CalorieCalc() {
  const [age,setAge]=useState("");const [w,setW]=useState("");const [h,setH]=useState("");const [g,setG]=useState("male");const [act,setAct]=useState(1.375);
  let tdee=null;
  if(age&&w&&h){const bmr=g==="male"?10*w+6.25*h-5*age+5:10*w+6.25*h-5*age-161;tdee=Math.round(bmr*act);}
  return (<div className="space-y-4"><div className="grid grid-cols-2 gap-3"><input className={inputCls} placeholder="Age" type="number" value={age} onChange={(e)=>setAge(e.target.value)} data-testid="cal-age" /><select className={inputCls} value={g} onChange={(e)=>setG(e.target.value)}><option value="male">Male</option><option value="female">Female</option></select></div><div className="grid grid-cols-2 gap-3"><input className={inputCls} placeholder="Weight (kg)" type="number" value={w} onChange={(e)=>setW(e.target.value)} data-testid="cal-weight" /><input className={inputCls} placeholder="Height (cm)" type="number" value={h} onChange={(e)=>setH(e.target.value)} /></div><select className={inputCls} value={act} onChange={(e)=>setAct(+e.target.value)}><option value={1.2}>Sedentary</option><option value={1.375}>Light activity</option><option value={1.55}>Moderate</option><option value={1.725}>Very active</option></select>{tdee && <Result>~{tdee} kcal / day (maintenance)</Result>}</div>);
}

function MacroCalc() {
  const [cals,setCals]=useState("");const [goal,setGoal]=useState("maintain");
  let out=null;
  if(cals){let c=+cals;if(goal==="cut")c-=400;if(goal==="bulk")c+=300;const p=Math.round(c*0.35/4),f=Math.round(c*0.25/9),cb=Math.round(c*0.4/4);out={c:Math.round(c),p,f,cb};}
  return (<div className="space-y-4"><input className={inputCls} placeholder="Maintenance calories" type="number" value={cals} onChange={(e)=>setCals(e.target.value)} data-testid="macro-cals" /><select className={inputCls} value={goal} onChange={(e)=>setGoal(e.target.value)}><option value="cut">Fat Loss</option><option value="maintain">Maintain</option><option value="bulk">Muscle Gain</option></select>{out && <div className="mt-5 grid grid-cols-3 gap-3">{[["Protein",out.p+"g"],["Carbs",out.cb+"g"],["Fat",out.f+"g"]].map(([l,v])=><div key={l} className="glass rounded-xl p-4 text-center"><p className="font-heading font-black text-xl text-accent">{v}</p><p className="text-xs uppercase tracking-widest text-muted-foreground">{l}</p></div>)}</div>}</div>);
}

function ProteinCalc() {
  const [w,setW]=useState("");const [lvl,setLvl]=useState(1.8);
  const g=w?Math.round(w*lvl):null;
  return (<div className="space-y-4"><input className={inputCls} placeholder="Bodyweight (kg)" type="number" value={w} onChange={(e)=>setW(e.target.value)} data-testid="protein-weight" /><select className={inputCls} value={lvl} onChange={(e)=>setLvl(+e.target.value)}><option value={1.6}>Maintenance (1.6g/kg)</option><option value={1.8}>Muscle Gain (1.8g/kg)</option><option value={2.2}>Fat Loss / Cut (2.2g/kg)</option></select>{g && <Result>{g}g protein / day</Result>}</div>);
}

function OneRMCalc() {
  const [w,setW]=useState("");const [r,setR]=useState("");
  const orm=w&&r?Math.round(w*(1+r/30)):null;
  return (<div className="space-y-4"><input className={inputCls} placeholder="Weight lifted (kg)" type="number" value={w} onChange={(e)=>setW(e.target.value)} data-testid="orm-weight" /><input className={inputCls} placeholder="Reps performed" type="number" value={r} onChange={(e)=>setR(e.target.value)} data-testid="orm-reps" />{orm && <Result>Estimated 1RM: {orm} kg</Result>}</div>);
}

function WaterCalc() {
  const [w,setW]=useState("");
  const l=w?(w*0.035).toFixed(1):null;
  return (<div className="space-y-4"><input className={inputCls} placeholder="Bodyweight (kg)" type="number" value={w} onChange={(e)=>setW(e.target.value)} data-testid="water-weight" />{l && <Result>{l} L water / day</Result>}</div>);
}

const calcs = [
  { id: "bmi", label: "BMI", icon: Scale, comp: <BMICalc /> },
  { id: "calorie", label: "Calorie", icon: Flame, comp: <CalorieCalc /> },
  { id: "macro", label: "Macro", icon: Activity, comp: <MacroCalc /> },
  { id: "protein", label: "Protein", icon: Beef, comp: <ProteinCalc /> },
  { id: "1rm", label: "1 Rep Max", icon: Dumbbell, comp: <OneRMCalc /> },
  { id: "water", label: "Water", icon: Droplet, comp: <WaterCalc /> },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHero label="Free Resources" title={<>Free tools to <span className="text-accent">get you started.</span></>}
        subtitle="Interactive calculators and downloadable guides — no strings attached." />

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionLabel index="01">Fitness Calculators</SectionLabel>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-10">Crunch your numbers.</h2>
          <Reveal>
            <div className="glass rounded-3xl p-6 md:p-8">
              <Tabs defaultValue="bmi">
                <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent mb-6 justify-start">
                  {calcs.map((c) => (
                    <TabsTrigger key={c.id} value={c.id} data-testid={`calc-tab-${c.id}`}
                      className="rounded-full data-[state=active]:bg-accent data-[state=active]:text-white px-4 py-2 gap-2">
                      <c.icon className="w-4 h-4" /> {c.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {calcs.map((c) => (
                  <TabsContent key={c.id} value={c.id} className="max-w-md">{c.comp}</TabsContent>
                ))}
              </Tabs>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <h3 className="font-heading font-black text-2xl md:text-3xl mb-4">More free resources coming soon.</h3>
          <p className="text-muted-foreground">Follow me on Instagram for weekly training and nutrition tips.</p>
        </div>
      </section>
    </div>
  );
}
